'use client';
import { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { LabelMark } from '../../components/ui/StatusMark';
import { Button } from '../../components/ui/Button';
import { IconButton } from '../../components/ui/IconButton';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/shadcn/textarea';
import { Field } from '../../components/ui/Field';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Toggle } from '../../components/ui/Toggle';
import { EmptyState } from '../../components/ui/states';
import { interpolate, useTranslation } from '../../lib/i18n';
import type { RolePolicy } from '../../lib/types';
import type { PluginConfigCommitResult } from '../../lib/usePluginConfigDraft';

/** Structured editor for a `rolePolicies` field. A role decides admission, the room prompt, and
 *  whether the sender is a platform administrator. Account identity and project/tool permissions come
 *  from the linked Elowen account, so legacy fields stored by older versions are deliberately ignored. */
export function RolePoliciesEditor({ value, onChange, onRemove, halted, owner, refused }: {
  value: RolePolicy[];
  onChange: (v: RolePolicy[]) => void;
  onRemove: (v: RolePolicy[]) => Promise<PluginConfigCommitResult>;
  /** The owning draft's writes are blocked until its recovery (a conflict or an unconfirmed write): a removal
   *  cannot be confirmed meanwhile, and the draft's recovery card is the way out. */
  halted: boolean;
  /** The owning draft's reporter identity (`draft.owner`). The draft reports a failed removal; the confirmation
   *  hosts that report while it is open instead of raising a second one. */
  owner: string;
  /** The daemon refused the submitted value: corrected, never toasted, so the confirmation says so itself. */
  refused: boolean;
}) {
  const { t } = useTranslation();
  const patch = (i: number, p: Partial<RolePolicy>) => onChange(value.map((r, j) => (j === i ? { ...r, ...p } : r)));
  // Which rows are expanded (by index). Removing a row shifts the indices above it down by one.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [removing, setRemoving] = useState<{ index: number; role: RolePolicy }>();
  const [activationPending, setActivationPending] = useState(false);
  const toggleRow = (i: number) => setExpanded((prev) => { const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n; });
  const addRole = () => { setExpanded((prev) => new Set(prev).add(value.length)); onChange([...value, { roleId: '', name: '', prompt: '' }]); };
  const removeRole = async () => {
    if (!removing) return;
    const i = removing.index;
    // A refusal rejects: the confirmation stays open and unlocked (confirming again is the retry of a refused
    // removal). The draft is the one reporter of its commits and the confirmation hosts that report (`reportedBy`).
    const outcome = await onRemove(value.filter((_, j) => j !== i));
    setActivationPending(outcome.pending);
    setExpanded((prev) => { const n = new Set<number>(); for (const idx of prev) { if (idx < i) n.add(idx); else if (idx > i) n.add(idx - 1); } return n; });
    setRemoving(undefined);
  };
  const removingName = removing ? (removing.role.name || removing.role.roleId || t.pluginCfg.roleNew) : '';
  return (
    <>
      <div className="flex flex-col gap-3">
        {activationPending ? <p className="text-ui text-warning" role="status">{t.pluginCfg.roleActivationPending}</p> : null}
        {value.length === 0 ? <EmptyState title={t.pluginCfg.noRoles} description={t.pluginCfg.noRolesHint} /> : null}
        {value.map((r, i) => {
          const open = expanded.has(i);
          return (
            <div key={i} className="rounded-lg border border-border bg-sunken">
              <div className="flex items-center gap-2 p-3">
                <Button type="button" variant="ghost" onClick={() => toggleRow(i)} aria-expanded={open} className="h-auto min-w-0 flex-1 justify-start gap-2 whitespace-normal px-0 py-0 text-left hover:bg-transparent">
                  {open ? <ChevronDown size={15} className="shrink-0 text-muted-foreground" aria-hidden /> : <ChevronRight size={15} className="shrink-0 text-muted-foreground" aria-hidden />}
                  <span className="truncate text-card-title text-foreground">{r.name || t.pluginCfg.roleNew}</span>
                  {r.roleId ? <span className="truncate font-mono text-small text-muted-foreground">{r.roleId}</span> : null}
                  {r.admin === true ? <span className="ml-auto shrink-0"><LabelMark tone="accent">{t.pluginCfg.roleAdminBadge}</LabelMark></span> : null}
                </Button>
                <IconButton variant="danger" icon={Trash2} label={t.pluginCfg.removeRole} onClick={() => setRemoving({ index: i, role: r })} />
              </div>
              {open ? (
                <div className="flex flex-col gap-3 border-t border-border p-3">
                  <div className="@container">
                    <div className="grid grid-cols-1 gap-3 @sm:grid-cols-2">
                      <Field label={t.pluginCfg.roleId}>
                        <Input value={r.roleId} onChange={(e) => patch(i, { roleId: e.target.value })} placeholder="1511041803225272420" className="font-mono" />
                      </Field>
                      <Field label={t.pluginCfg.roleName}>
                        <Input value={r.name} onChange={(e) => patch(i, { name: e.target.value })} placeholder="dev-team" />
                      </Field>
                    </div>
                  </div>
                  <label className="flex cursor-pointer items-center gap-2.5">
                    <Toggle checked={r.admin === true} onChange={(v) => patch(i, { admin: v })} label={t.pluginCfg.roleAdmin} />
                    <span className="flex flex-col">
                      <span className="text-ui text-foreground">{t.pluginCfg.roleAdmin}</span>
                      <span className="text-small text-muted-foreground">{t.pluginCfg.roleAdminHint}</span>
                    </span>
                  </label>
                  <Field label={t.pluginCfg.rolePrompt} hint={t.help.rolePrompt}>
                    <Textarea value={r.prompt} onChange={(e) => patch(i, { prompt: e.target.value })} rows={3} />
                  </Field>
                </div>
              ) : null}
            </div>
          );
        })}
        <Button variant="ghost" icon={Plus} className="self-start" onClick={addRole}>
          {t.pluginCfg.addRole}
        </Button>
      </div>
      <ConfirmDialog
        open={Boolean(removing)}
        title={interpolate(t.pluginCfg.removeRoleTitle, { name: removingName })}
        description={interpolate(t.pluginCfg.removeRoleDescription, { name: removingName })}
        confirmLabel={t.pluginCfg.removeRole}
        pendingLabel={t.pluginCfg.removingRole}
        confirmDisabled={halted}
        reportedBy={owner}
        onClose={() => setRemoving(undefined)}
        onConfirm={removeRole}
      >
        {refused ? <p role="alert" className="text-meta text-destructive">{t.pluginCfg.valueRefused}</p> : null}
      </ConfirmDialog>
    </>
  );
}
